import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  LazyMotion,
  MotionConfig,
  m,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  BrainCircuit,
  Compass,
  ExternalLink,
  Instagram,
  Leaf,
  Mail,
  Menu,
  Microscope,
  Moon,
  MoveUpRight,
  ShieldCheck,
  Sparkles,
  Sun,
  X,
} from "lucide-react";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { useSiteLanguage } from "@/features/preferences/SiteLanguageContext";
import { useSiteSeo } from "@/lib/seo";
import { BLOG_CATALOG } from "./blogCatalog";
import {
  HOME_CONTENT,
  HOME_THEME_STORAGE_KEY,
} from "./homeLandingContent";
import "./HomeLandingPage.css";

const PILLAR_ICONS = [Leaf, Compass, Sparkles];
const TRUST_ICONS = [BookOpen, BarChart3, ShieldCheck];
const SUPPORTED_THEMES = ["dark", "light"];
const SectionMotionContext = createContext(null);
const ReducedMotionContext = createContext(false);
const loadMotionFeatures = () => import("./homeMotionFeatures").then((module) => module.default);

const MOTION_SPRING = {
  stiffness: 115,
  damping: 28,
  mass: 0.32,
};

export const JELLYFISH_CONFIG = {
  inicio: { variant: "filament", side: "right", speed: 0.42, direction: 1, scrollX: -0.16, size: "small", drift: "soft-rise" },
  manifiesto: { variant: "moon", side: "left", speed: 0.62, direction: -1, scrollX: 0.12, size: "medium", drift: "wide-tide" },
  bienestar: { variant: "filament", side: "right", speed: 0.78, direction: 1, scrollX: -0.22, size: "small", drift: "deep-pulse" },
  evidencia: { variant: "moon", side: "left", speed: 0.9, direction: -1, scrollX: 0.16, size: "medium", drift: "slow-orbit" },
  innovacion: { variant: "filament", side: "left", speed: 0.7, direction: 1, scrollX: 0.25, size: "small", drift: "diagonal-glide" },
  nosotros: { variant: "moon", side: "right", speed: 0.66, direction: -1, scrollX: -0.14, size: "medium", drift: "long-wave" },
  blogs: { variant: "filament", side: "left", speed: 0.74, direction: 1, scrollX: 0.19, size: "small", drift: "editorial-current" },
  confianza: { variant: "filament", side: "left", speed: 0.54, direction: 1, scrollX: 0.09, size: "small", drift: "short-bloom" },
  cierre: { variant: "moon", side: "right", speed: 0.82, direction: -1, scrollX: -0.2, size: "medium", drift: "closing-current" },
};

function readStoredPreference(key, allowedValues, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const storedValue = window.localStorage.getItem(key);
    return allowedValues.includes(storedValue) ? storedValue : fallback;
  } catch (_error) {
    return fallback;
  }
}

function persistPreference(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (_error) {
    // The preference remains active for this visit when storage is unavailable.
  }
}

const JELLYFISH_ASSETS = {
  moon: {
    src: "/assets/kavia-jellyfish-moon.webp",
    srcSet: "/assets/kavia-jellyfish-moon-256.webp 256w, /assets/kavia-jellyfish-moon.webp 480w",
    width: 480,
    height: 720,
  },
  filament: {
    src: "/assets/kavia-jellyfish-filament.webp",
    srcSet: "/assets/kavia-jellyfish-filament-256.webp 256w, /assets/kavia-jellyfish-filament.webp 480w",
    width: 480,
    height: 873,
  },
};

function readReducedMotionPreference() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(readReducedMotionPreference);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = (event) => setPrefersReducedMotion(event.matches);
    setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", updatePreference);
    return () => mediaQuery.removeEventListener?.("change", updatePreference);
  }, []);

  return prefersReducedMotion;
}

export function getSectionMotionKeyframes(distance = 64) {
  const safeDistance = Math.min(Math.max(Number(distance) || 0, 36), 96);
  return {
    input: [0, 0.16, 0.86, 1],
    y: [safeDistance, 0, 0, -safeDistance],
    opacity: [0.2, 1, 1, 0.28],
    scale: [0.985, 1, 1, 0.99],
  };
}

export function getJellyfishMotionKeyframes({ speed = 0.7, direction = 1, scrollX = 0 }) {
  const safeSpeed = Math.min(Math.max(Number(speed) || 0, 0), 1.2);
  const safeDirection = Number(direction) < 0 ? -1 : 1;
  const safeScrollX = Math.min(Math.max(Number(scrollX) || 0, -0.3), 0.3);
  const verticalTravel = Math.min(Math.round(safeSpeed * 88), 80) * safeDirection;
  const horizontalTravel = Math.round(safeScrollX * 96);

  return {
    input: [0, 0.5, 1],
    x: [-horizontalTravel, 0, horizontalTravel],
    y: [-verticalTravel, 0, verticalTravel],
  };
}

function ActionArrow({ size = 17 }) {
  return <ArrowRight size={size} strokeWidth={1.8} aria-hidden="true" />;
}

function AnimatedScrollSection({ children, motionDistance, ...sectionProps }) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 92%", "end 8%"],
  });
  const smoothProgress = useSpring(scrollYProgress, MOTION_SPRING);
  const keyframes = getSectionMotionKeyframes(motionDistance);
  const y = useTransform(smoothProgress, keyframes.input, keyframes.y);
  const opacity = useTransform(smoothProgress, keyframes.input, keyframes.opacity);
  const scale = useTransform(smoothProgress, keyframes.input, keyframes.scale);

  return (
    <SectionMotionContext.Provider value={smoothProgress}>
      <m.section
        {...sectionProps}
        ref={sectionRef}
        data-scroll-section
        data-motion-disabled="false"
        data-motion-distance={motionDistance}
        style={{ opacity, scale, y }}
      >
        {children}
      </m.section>
    </SectionMotionContext.Provider>
  );
}

function ScrollSection({ children, motionDistance = 64, ...sectionProps }) {
  const prefersReducedMotion = useContext(ReducedMotionContext);

  if (prefersReducedMotion) {
    return (
      <SectionMotionContext.Provider value={null}>
        <section
          {...sectionProps}
          data-scroll-section
          data-motion-disabled="true"
          data-motion-distance={motionDistance}
        >
          {children}
        </section>
      </SectionMotionContext.Provider>
    );
  }

  return (
    <AnimatedScrollSection {...sectionProps} motionDistance={motionDistance}>
      {children}
    </AnimatedScrollSection>
  );
}

function JellyfishImage({ asset }) {
  return (
    <span className="kavia-home__jelly-drift">
      <img
        src={asset.src}
        srcSet={asset.srcSet}
        sizes="(max-width: 640px) 18vw, 9rem"
        alt=""
        width={asset.width}
        height={asset.height}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
      />
    </span>
  );
}

function AnimatedJellyfish({ asset, config, progress }) {
  const keyframes = getJellyfishMotionKeyframes(config);
  const x = useTransform(progress, keyframes.input, keyframes.x);
  const y = useTransform(progress, keyframes.input, keyframes.y);

  return (
    <m.span
      className="kavia-home__jelly-parallax"
      data-ambient-animation
      data-floating-jellyfish
      data-motion-disabled="false"
      data-scroll-speed={config.speed}
      data-scroll-x={config.scrollX}
      data-direction={config.direction}
      style={{ x, y }}
    >
      <JellyfishImage asset={asset} />
    </m.span>
  );
}

function StaticJellyfish({ asset, config }) {
  return (
    <span
      className="kavia-home__jelly-parallax"
      data-ambient-animation
      data-floating-jellyfish
      data-motion-disabled="true"
      data-scroll-speed={config.speed}
      data-scroll-x={config.scrollX}
      data-direction={config.direction}
    >
      <JellyfishImage asset={asset} />
    </span>
  );
}

function SectionJellyfish({ zone }) {
  const progress = useContext(SectionMotionContext);
  const config = JELLYFISH_CONFIG[zone];
  const asset = JELLYFISH_ASSETS[config.variant];

  return (
    <span
      className="kavia-home__section-jelly"
      data-jelly-zone={zone}
      data-side={config.side}
      data-size={config.size}
      data-drift-profile={config.drift}
      data-motion-signature={`${config.side}:${config.speed}:${config.scrollX}:${config.drift}`}
      aria-hidden="true"
    >
      {progress
        ? <AnimatedJellyfish asset={asset} config={config} progress={progress} />
        : <StaticJellyfish asset={asset} config={config} />}
    </span>
  );
}

function StaticFloatingAtmosphere() {
  return (
    <div className="kavia-home__floating-layer" data-motion-disabled="true" aria-hidden="true">
      <span className="kavia-home__floater kavia-home__floater--orb" data-floating-element data-speed="0.9" />
      <span className="kavia-home__floater kavia-home__floater--capsule" data-floating-element data-speed="-0.7" />
      <span className="kavia-home__floater kavia-home__floater--diamond" data-floating-element data-speed="0.55" />
      <span className="kavia-home__floater kavia-home__floater--membrane" data-floating-element data-speed="-0.45" />
      <span className="kavia-home__knowledge-current" data-scroll-progress><span /></span>
    </div>
  );
}

function AnimatedFloatingAtmosphere() {
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 95, damping: 28, mass: 0.28 });
  const waveInput = [0, 0.25, 0.5, 0.75, 1];
  const floaters = [
    {
      className: "kavia-home__floater--orb",
      speed: "0.9",
      x: useTransform(smoothProgress, waveInput, [0, 46, 0, -46, 0]),
      y: useTransform(smoothProgress, [0, 1], [0, 330]),
      rotate: useTransform(smoothProgress, [0, 1], [0, 190]),
    },
    {
      className: "kavia-home__floater--capsule",
      speed: "-0.7",
      x: useTransform(smoothProgress, waveInput, [0, -58, 0, 58, 0]),
      y: useTransform(smoothProgress, [0, 1], [0, -260]),
      rotate: useTransform(smoothProgress, [0, 1], [0, -145]),
    },
    {
      className: "kavia-home__floater--diamond",
      speed: "0.55",
      x: useTransform(smoothProgress, waveInput, [0, -32, 0, 32, 0]),
      y: useTransform(smoothProgress, [0, 1], [0, 220]),
      rotate: useTransform(smoothProgress, [0, 1], [0, 260]),
    },
    {
      className: "kavia-home__floater--membrane",
      speed: "-0.45",
      x: useTransform(smoothProgress, waveInput, [0, 62, 0, -62, 0]),
      y: useTransform(smoothProgress, [0, 1], [0, -180]),
      rotate: useTransform(smoothProgress, [0, 1], [0, 36]),
    },
  ];

  return (
    <div className="kavia-home__floating-layer" data-motion-disabled="false" aria-hidden="true">
      {floaters.map(({ className, rotate, speed, x, y }) => (
        <m.span
          className={`kavia-home__floater ${className}`}
          data-floating-element
          data-speed={speed}
          key={className}
          style={{ rotate, x, y }}
        />
      ))}
      <span className="kavia-home__knowledge-current" data-scroll-progress>
        <m.span style={{ scaleY: smoothProgress }} />
      </span>
    </div>
  );
}

function FloatingAtmosphere() {
  const prefersReducedMotion = useContext(ReducedMotionContext);
  return prefersReducedMotion ? <StaticFloatingAtmosphere /> : <AnimatedFloatingAtmosphere />;
}

export function HomeLandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { language } = useSiteLanguage();
  const [theme, setTheme] = useState(() => readStoredPreference(HOME_THEME_STORAGE_KEY, SUPPORTED_THEMES, "dark"));
  const prefersReducedMotion = usePrefersReducedMotion();
  const pageRef = useRef(null);
  const copy = HOME_CONTENT[language];
  const blogs = BLOG_CATALOG[language];
  const themeColor = theme === "light" ? "#e8f2ff" : "#070c12";

  useSiteSeo({
    ...copy.seo,
    canonical: "https://kaviastore.co/",
    locale: copy.locale,
    themeColor,
  });

  useEffect(() => {
    document.body.classList.add("kavia-home-active");
    return () => document.body.classList.remove("kavia-home-active");
  }, []);

  useEffect(() => {
    const previousColorScheme = document.documentElement.style.colorScheme;
    document.body.dataset.kaviaHomeTheme = theme;
    document.documentElement.style.colorScheme = theme;
    persistPreference(HOME_THEME_STORAGE_KEY, theme);
    return () => {
      delete document.body.dataset.kaviaHomeTheme;
      document.documentElement.style.colorScheme = previousColorScheme;
    };
  }, [theme]);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const ambientNodes = [...page.querySelectorAll("[data-ambient-animation]")];
    const visual = page.querySelector("[data-organic-visual]");

    if (!("IntersectionObserver" in window)) {
      ambientNodes.forEach((node) => node.classList.add("is-ambient-active"));
      visual?.classList.add("is-in-view");
      return undefined;
    }

    const visualObserver = new IntersectionObserver(([entry]) => {
      visual?.classList.toggle("is-in-view", entry.isIntersecting);
    }, { rootMargin: "10% 0px", threshold: 0.05 });

    const ambientObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle("is-ambient-active", entry.isIntersecting);
      });
    }, { rootMargin: "12% 0px", threshold: 0.01 });

    if (visual) visualObserver.observe(visual);
    ambientNodes.forEach((node) => ambientObserver.observe(node));
    return () => {
      visualObserver.disconnect();
      ambientObserver.disconnect();
    };
  }, []);

  const closeMenu = () => setMenuOpen(false);
  const toggleTheme = () => setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
  const themeActionLabel = theme === "dark" ? copy.controls.switchToLight : copy.controls.switchToDark;

  return (
    <ReducedMotionContext.Provider value={prefersReducedMotion}>
      <MotionConfig reducedMotion="user" isStatic={process.env.NODE_ENV === "test"}>
        <LazyMotion features={loadMotionFeatures} strict>
          <div className="kavia-home" data-theme={theme} ref={pageRef}>
            <a className="kavia-home__skip" href="#contenido">{copy.accessibility.skip}</a>

            <FloatingAtmosphere />

            <div className="kavia-home__shell">
        <header className="kavia-home__header" id="inicio">
          <a className="kavia-home__brand" href="/" aria-label={copy.accessibility.home}>
            <img src="/logo-kavia.png" alt="KaviaStore" width="149" height="50" />
          </a>

          <nav className="kavia-home__nav" aria-label={copy.accessibility.primaryNavigation}>
            {copy.nav.map(({ href, id, label }) => <a href={href} key={id}>{label}</a>)}
          </nav>

          <div className="kavia-home__header-actions">
            <LanguageSelector label={copy.controls.language} onLanguageChange={closeMenu} />

            <button
              type="button"
              className="kavia-home__theme-button"
              aria-label={themeActionLabel}
              title={themeActionLabel}
              onClick={toggleTheme}
            >
              {theme === "dark" ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
            </button>

            <a className="kavia-home__header-cta" href="#evidencia">
              <BarChart3 size={17} strokeWidth={1.8} aria-hidden="true" />
              <span>{copy.dataCta}</span>
            </a>

            <button
              type="button"
              className="kavia-home__menu-button"
              aria-expanded={menuOpen}
              aria-controls="kavia-mobile-navigation"
              aria-label={menuOpen ? copy.accessibility.closeMenu : copy.accessibility.openMenu}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
            </button>
          </div>
        </header>

        <nav
          className={`kavia-home__mobile-nav${menuOpen ? " is-open" : ""}`}
          id="kavia-mobile-navigation"
          aria-label={copy.accessibility.mobileNavigation}
          aria-hidden={!menuOpen}
        >
          {copy.nav.map(({ href, id, label }) => (
            <a href={href} key={id} onClick={closeMenu} tabIndex={menuOpen ? 0 : -1}>{label}</a>
          ))}
          <a href="#evidencia" onClick={closeMenu} tabIndex={menuOpen ? 0 : -1}>{copy.mobileDataCta} <ActionArrow /></a>
        </nav>

        <main id="contenido">
          <ScrollSection className="kavia-home__hero" aria-labelledby="home-hero-title" motionDistance={54}>
            <SectionJellyfish zone="inicio" />
            <div className="kavia-home__hero-copy">
              <p className="kavia-home__eyebrow"><span /> {copy.hero.eyebrow}</p>
              <h1 id="home-hero-title">
                {copy.hero.title[0]}<br />
                {copy.hero.title[1]}<br />
                <em>{copy.hero.title[2]}</em>
              </h1>
              <p className="kavia-home__hero-description">
                {copy.hero.description}
              </p>
              <div className="kavia-home__hero-actions">
                <a className="kavia-home__button kavia-home__button--primary" href="#bienestar">
                  {copy.hero.primaryCta} <ActionArrow />
                </a>
                <a className="kavia-home__button kavia-home__button--secondary" href="#nosotros">
                  {copy.hero.secondaryCta}
                </a>
              </div>

              <div className="kavia-home__pillars" aria-label={copy.accessibility.principles}>
                {copy.hero.pillars.map(({ id, title, text }, index) => {
                  const Icon = PILLAR_ICONS[index];
                  return (
                  <article className="kavia-home__micro-card" key={id}>
                    <Icon size={18} strokeWidth={1.6} aria-hidden="true" />
                    <div>
                      <h2>{title}</h2>
                      <p>{text}</p>
                    </div>
                  </article>
                  );
                })}
              </div>
            </div>

            <div className="kavia-home__visual" data-organic-visual aria-hidden="true">
              <span className="kavia-home__visual-halo" />
              <span className="kavia-home__orbit kavia-home__orbit--one" />
              <span className="kavia-home__orbit kavia-home__orbit--two" />
              <picture>
                <source srcSet="/assets/kavia-organic-evolution.avif" type="image/avif" />
                <img
                  src="/assets/kavia-organic-evolution-800.webp"
                  alt=""
                  width="800"
                  height="1414"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
            </div>

            <aside className="kavia-home__hero-rail" aria-label={copy.accessibility.wellbeingData}>
              <a className="kavia-home__insight-card" href="#evidencia">
                <span className="kavia-home__card-index">{copy.hero.insight.index}</span>
                <strong>{copy.hero.insight.value}<small>%</small></strong>
                <p>{copy.hero.insight.text}</p>
                <span className="kavia-home__insight-link">{copy.hero.insight.link} <MoveUpRight size={18} aria-hidden="true" /></span>
              </a>

              <a className="kavia-home__vision-card" href="#innovacion">
                <span className="kavia-home__card-index">{copy.hero.vision.index}</span>
                <strong>{copy.hero.vision.text}</strong>
                <span className="kavia-home__round-arrow"><ActionArrow /></span>
              </a>
            </aside>
          </ScrollSection>

          <ScrollSection className="kavia-home__manifesto" aria-labelledby="manifesto-title" motionDistance={68}>
            <SectionJellyfish zone="manifiesto" />
            <div className="kavia-home__section-label">
              <span>01</span>
              <p>{copy.manifesto.label}</p>
            </div>
            <div className="kavia-home__manifesto-copy">
              <h2 id="manifesto-title">
                {copy.manifesto.titleStart}<em>{copy.manifesto.titleAction}</em>{copy.manifesto.titleMiddle}<em>{copy.manifesto.titleCare}</em>
              </h2>
              <p>{copy.manifesto.text}</p>
            </div>
          </ScrollSection>

          <ScrollSection className="kavia-home__wellbeing" id="bienestar" aria-labelledby="wellbeing-title" motionDistance={62}>
            <SectionJellyfish zone="bienestar" />
            <div className="kavia-home__section-intro">
              <div className="kavia-home__section-label">
                <span>02</span>
                <p>{copy.wellbeing.label}</p>
              </div>
              <div>
                <p className="kavia-home__eyebrow"><span /> {copy.wellbeing.eyebrow}</p>
                <h2 id="wellbeing-title">{copy.wellbeing.title}</h2>
              </div>
            </div>

            <div className="kavia-home__wellbeing-grid">
              <article className="kavia-home__editorial-card kavia-home__editorial-card--wide" data-ambient-animation>
                <span className="kavia-home__card-index">{copy.wellbeing.dailyIndex}</span>
                <p>{copy.wellbeing.dailyText}</p>
                <div className="kavia-home__signal" aria-hidden="true"><i /><i /><i /><i /></div>
              </article>

              <article className="kavia-home__editorial-card kavia-home__editorial-card--accent">
                <Compass aria-hidden="true" />
                <span className="kavia-home__card-index">{copy.wellbeing.processIndex}</span>
                <h3>{copy.wellbeing.processTitle}</h3>
                <p>{copy.wellbeing.processText}</p>
              </article>

              <article className="kavia-home__editorial-card kavia-home__editorial-card--future" data-ambient-animation>
                <div className="kavia-home__future-visual" aria-hidden="true">
                  <span /><span /><span />
                  <BrainCircuit />
                </div>
                <div>
                  <span>{copy.wellbeing.appliedKnowledge}</span>
                  <h3>{copy.wellbeing.futureTitle}</h3>
                </div>
              </article>

              <article className="kavia-home__editorial-card kavia-home__editorial-card--vision">
                <span className="kavia-home__card-index">{copy.wellbeing.visionIndex}</span>
                <h3>{copy.wellbeing.visionTitle}</h3>
                <p>{copy.wellbeing.visionText}</p>
                <small>{copy.wellbeing.visionNote}</small>
              </article>
            </div>
          </ScrollSection>

          <ScrollSection className="kavia-home__evidence" id="evidencia" aria-labelledby="evidence-title" motionDistance={78}>
            <SectionJellyfish zone="evidencia" />
            <div className="kavia-home__evidence-heading">
              <div className="kavia-home__section-label">
                <span>03</span>
                <p>{copy.evidence.label}</p>
              </div>
              <div>
                <p className="kavia-home__eyebrow"><span /> {copy.evidence.eyebrow}</p>
                <h2 id="evidence-title">{copy.evidence.title}</h2>
                <p>{copy.evidence.intro}</p>
              </div>
            </div>

            <div className="kavia-home__stats-grid">
              {copy.evidence.stats.map((stat, index) => (
                <article
                  className={`kavia-home__stat-card kavia-home__stat-card--${stat.tone}`}
                  data-stat-id={stat.id}
                  key={stat.id}
                >
                  <span className="kavia-home__stat-index">0{index + 1}</span>
                  <div className="kavia-home__stat-value">{stat.value}</div>
                  <p className="kavia-home__stat-label">{stat.label}</p>
                  <h3>{stat.title}</h3>
                  <p>{stat.text}</p>
                  <a href={stat.href} target="_blank" rel="noreferrer">
                    {stat.source} <ExternalLink size={14} aria-hidden="true" />
                  </a>
                </article>
              ))}
            </div>

            <p className="kavia-home__evidence-note">{copy.evidence.note}</p>
          </ScrollSection>

          <ScrollSection className="kavia-home__innovation" id="innovacion" aria-labelledby="innovation-title" motionDistance={66}>
            <SectionJellyfish zone="innovacion" />
            <div className="kavia-home__innovation-number" aria-hidden="true">
              <span>≈</span>70<small>%</small>
            </div>
            <div className="kavia-home__innovation-copy">
              <p className="kavia-home__eyebrow"><span /> {copy.innovation.eyebrow}</p>
              <h2 id="innovation-title">{copy.innovation.title}</h2>
              <p>{copy.innovation.textOne}</p>
              <p>{copy.innovation.textTwo}</p>
              <div className="kavia-home__source-links">
                <a href="https://www.who.int/health-topics/diagnostics" target="_blank" rel="noreferrer">
                  <Microscope aria-hidden="true" /> {copy.innovation.diagnostics} <ExternalLink size={13} aria-hidden="true" />
                </a>
                <a href="https://www.who.int/health-topics/genomics" target="_blank" rel="noreferrer">
                  <BrainCircuit aria-hidden="true" /> {copy.innovation.genomics} <ExternalLink size={13} aria-hidden="true" />
                </a>
              </div>
            </div>
          </ScrollSection>

          <ScrollSection className="kavia-home__about" id="nosotros" aria-labelledby="about-title" motionDistance={72}>
            <SectionJellyfish zone="nosotros" />
            <div className="kavia-home__about-heading">
              <div className="kavia-home__section-label">
                <span>04</span>
                <p>{copy.about.label}</p>
              </div>
              <p className="kavia-home__eyebrow"><span /> {copy.about.eyebrow}</p>
              <h2 id="about-title">{copy.about.title}</h2>
            </div>

            <div className="kavia-home__about-grid">
              {copy.about.cards.map(({ id, title, text }, index) => (
                <article key={id}>
                  <span className="kavia-home__about-number">0{index + 1}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </ScrollSection>

          <ScrollSection className="kavia-home__blogs" id="blogs" aria-labelledby="blogs-title" motionDistance={74}>
            <SectionJellyfish zone="blogs" />
            <div className="kavia-home__blogs-heading">
              <div className="kavia-home__section-label">
                <span>05</span>
                <p>{copy.blogs.label}</p>
              </div>
              <div>
                <p className="kavia-home__eyebrow"><span /> {copy.blogs.eyebrow}</p>
                <h2 id="blogs-title">{copy.blogs.title}</h2>
                <p>{copy.blogs.intro}</p>
              </div>
            </div>

            <ol className="kavia-home__blog-list" aria-label={copy.accessibility.blogList}>
              {blogs.map((article, index) => (
                <li key={article.id}>
                  <article className="kavia-home__blog-card" data-blog-card={article.id}>
                    <span className="kavia-home__blog-number" aria-hidden="true">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div className="kavia-home__blog-copy">
                      <div className="kavia-home__blog-meta">
                        <span>{article.category}</span>
                        <time dateTime={article.publishedAt}>{article.publishedLabel}</time>
                        <span>{article.readingTime}</span>
                      </div>
                      <h3>{article.title}</h3>
                      <p>{article.description}</p>
                    </div>
                    <a
                      className="kavia-home__blog-link"
                      href={article.href}
                      aria-label={`${copy.blogs.readArticle}: ${article.title}`}
                    >
                      {copy.blogs.readArticle}
                      <span><ActionArrow /></span>
                    </a>
                  </article>
                </li>
              ))}
            </ol>
          </ScrollSection>

          <ScrollSection className="kavia-home__trust" aria-label={copy.accessibility.interpretation} motionDistance={58}>
            <SectionJellyfish zone="confianza" />
            {copy.trust.map(({ id, title, text }, index) => {
              const Icon = TRUST_ICONS[index];
              return (
                <div key={id}>
                  <Icon aria-hidden="true" />
                  <span>{title}</span>
                  <p>{text}</p>
                </div>
              );
            })}
          </ScrollSection>

          <ScrollSection className="kavia-home__closing" aria-labelledby="closing-title" motionDistance={82}>
            <SectionJellyfish zone="cierre" />
            <span className="kavia-home__closing-orbit" aria-hidden="true" />
            <p className="kavia-home__eyebrow"><span /> {copy.closing.eyebrow}</p>
            <h2 id="closing-title">{copy.closing.title}</h2>
            <p>{copy.closing.text}</p>
            <a className="kavia-home__button kavia-home__button--light" href="#bienestar">
              {copy.closing.cta} <ActionArrow />
            </a>
          </ScrollSection>
        </main>

        <footer className="kavia-home__footer">
          <div className="kavia-home__footer-brand">
            <img src="/logo-kavia.png" alt="KaviaStore" width="149" height="50" />
            <p>{copy.footer.tagline[0]}<br />{copy.footer.tagline[1]}</p>
          </div>
          <nav aria-label={copy.accessibility.footerNavigation}>
            {copy.nav.slice(1).map(({ href, id, label }) => <a href={href} key={id}>{label}</a>)}
          </nav>
          <div className="kavia-home__footer-contact">
            <a href="mailto:kaviastore2026@gmail.com"><Mail size={17} aria-hidden="true" /> kaviastore2026@gmail.com</a>
            <a href="https://www.instagram.com/kaviastore_/" target="_blank" rel="noreferrer">
              <Instagram size={17} aria-hidden="true" /> @kaviastore_
            </a>
          </div>
          <p className="kavia-home__medical-note">{copy.footer.medicalNote}</p>
          <div className="kavia-home__footer-bottom">
            <span>© {new Date().getFullYear()} KaviaStore</span>
            <span>kaviastore.co</span>
          </div>
        </footer>
            </div>
          </div>
        </LazyMotion>
      </MotionConfig>
    </ReducedMotionContext.Provider>
  );
}
